'use client';

import { Skeleton, Spinner } from '@common/components';
import { cn } from '@core/utils';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions, UnauthQueryOptions } from '@/api';
import { GiftListItem } from '@/app/_components/DonationTemp/GiftListItem';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';

import { SearchPrepareSection } from './SearchPrepareSection';

interface SearchedSectionProps {
  searchKeyword: string;
  onHistoryClick?: (keyword: string, category: '최근 검색어') => void;
  onRecommendClick?: (keyword: string, category: '추천 검색어') => void;
}

export const SearchedSection = ({
  searchKeyword,
  onHistoryClick,
  onRecommendClick,
}: SearchedSectionProps) => {
  const { ref, inView } = useInView();
  const searchParams = useSearchParams();
  const searchCategory = searchParams.get('category') ?? undefined;

  //* 동일, 처음 문자열 일치하는 검색 결과 조회 (1)
  const {
    data: firstData,
    fetchNextPage: fetchNextFirstPage,
    hasNextPage: hasNextFirstPage,
    isFetching: isFetchingFirst,
    isLoading: isLoadingFirst,
  } = useInfiniteQuery({
    ...UnauthInfiniteQueryOptions[
      'get:/wello/v2/allows/home/gift-product/search'
    ]({
      query: {
        page: INITIAL_PAGE,
        size: PAGE_SIZE,
        searchKeyword: searchKeyword,
      },
    }),
    getNextPageParam: (lastPage) => {
      return lastPage.context?.hasNext ? lastPage.context.page + 1 : undefined;
    },
    enabled: Boolean(searchKeyword),
    initialPageParam: INITIAL_PAGE,
  });

  //* 동일, 처음 문자열 일치 제외 검색 결과 조회 (2)
  const {
    data: secondData,
    fetchNextPage: fetchNextSecondPage,
    hasNextPage: hasNextSecondPage,
    isFetching: isFetchingSecond,
  } = useInfiniteQuery({
    ...UnauthInfiniteQueryOptions[
      'get:/wello/v2/allows/home/gift-product/search/contains'
    ]({
      query: {
        page: INITIAL_PAGE,
        size: PAGE_SIZE,
        searchKeyword: searchKeyword,
      },
    }),
    getNextPageParam: (lastPage) => {
      return lastPage.context?.hasNext ? lastPage.context.page + 1 : undefined;
    },
    initialPageParam: INITIAL_PAGE,
    enabled: Boolean(searchKeyword) && !hasNextFirstPage && !isFetchingFirst,
  });

  useEffect(() => {
    if (!inView) return;

    if (hasNextFirstPage && !isFetchingFirst) {
      fetchNextFirstPage();
    } else if (hasNextSecondPage && !isFetchingSecond) {
      fetchNextSecondPage();
    }
  }, [
    inView,
    fetchNextFirstPage,
    hasNextFirstPage,
    fetchNextSecondPage,
    hasNextSecondPage,
    isFetchingFirst,
    isFetchingSecond,
  ]);

  //* 검색 결과 총 개수
  const { data: totalCount, isLoading: isLoadingTotalCount } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home/gift-product/search/total-count'
    ]({
      query: {
        searchKeyword: searchKeyword,
      },
    }),
    select: (data) => data.context?.totalCount?.toLocaleString() ?? 0,
  });

  const splitSearchKeyword = searchKeyword.split(' ');
  const firstGiftList =
    firstData?.pages.flatMap((page) => [
      ...(page.context?.exactMatchGiftProductList ?? []),
      ...(page.context?.contents ?? []),
    ]) ?? [];
  const secondGiftList =
    secondData?.pages.flatMap((page) => [
      ...(page.context?.exactMatchGiftProductList ?? []),
      ...(page.context?.contents ?? []),
    ]) ?? [];
  const giftList = [...firstGiftList, ...secondGiftList];
  const isEmpty = giftList.length < 1 && !isLoadingFirst;

  useEffect(() => {
    if (!searchKeyword || !searchCategory) {
      return;
    }

    const allQueriesLoaded =
      !isLoadingFirst && !isFetchingFirst && !isFetchingSecond;

    if (!allQueriesLoaded) return;

    const hasResults = giftList.length > 0;

    trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
      search_type: '고향사랑기부',
      search_result: hasResults ? 'Y' : 'N',
      search_term: searchKeyword,
      search_category: searchCategory,
    });
  }, [
    searchKeyword,
    searchCategory,
    isLoadingFirst,
    isFetchingFirst,
    isFetchingSecond,
    giftList.length,
  ]);

  return (
    <section
      className={cn(
        'flex h-full flex-col px-40 pb-32 content-container',
        'mobile-tablet:px-0',
      )}
    >
      {!isEmpty ? (
        <>
          <p
            className={cn(
              'px-20 py-60 text-center font-500 text-Gray-600 typo-h5',
              'mobile-tablet:mb-16 mobile-tablet:border-b mobile-tablet:border-Gray-200 mobile-tablet:py-12 mobile-tablet:text-left mobile-tablet:font-400 mobile-tablet:typo-body4',
            )}
          >
            {splitSearchKeyword.map((keyword, index) => (
              <span
                key={index}
                className={cn('font-800', 'mobile-tablet:font-700')}
              >
                &apos;{keyword}&apos;
                <span
                  className={cn(
                    index === splitSearchKeyword.length - 1 && 'hidden',
                  )}
                >
                  ,{' '}
                </span>
              </span>
            ))}{' '}
            에 대한 검색결과
          </p>

          <p
            className={cn(
              'p-0 pb-16 font-400 text-Gray-black typo-body3',
              'mobile-tablet:px-20 mobile-tablet:py-10 mobile-tablet:font-500 mobile-tablet:typo-body4',
            )}
          >
            {isLoadingTotalCount ? (
              <Skeleton className="h-21 w-50 rounded-pill" />
            ) : (
              `${totalCount}개 답례품`
            )}
          </p>

          <ul
            className={cn(
              'grid grid-cols-4 gap-x-16 gap-y-32',
              'mobile-tablet:px-20 mobile-tablet:pt-12',
              'tablet:grid-cols-3',
              'mobile:grid-cols-2',
            )}
          >
            {giftList.map(
              ({
                productNumber: product_number,
                mainImageUrl: main_image_url,
                productName: product_name,
                requiredDonationAmount: required_donation_amount,
                reviewRating: review_rating,
                reviewCount: review_count,
                institutionLogoUrl: institution_logo_url,
                institutionName: institution_name,
                institutionId: institution_id,
                adultYN: adult_yn,
                newDisplayYN: new_display_yn,
                productSaleStatus: product_sale_status,
              }) => (
                <li key={product_number}>
                  <GiftListItem
                    key={product_number}
                    mainImageSizes={`${BREAKPOINTS.MOBILE} 50vw, ${BREAKPOINTS.TABLET} 33vw, 288px`}
                    mainImageUrl={main_image_url}
                    productName={product_name}
                    donationAmount={required_donation_amount}
                    reviewCount={review_count}
                    reviewRating={review_rating}
                    institution={{
                      id: institution_id,
                      logoUrl: institution_logo_url,
                      name: institution_name,
                    }}
                    productNumber={product_number}
                    isNew={new_display_yn}
                    isAdult={adult_yn}
                    isSoldOut={
                      product_sale_status === GIFT_SALE_STATUS.SOLD_OUT
                    }
                    responsiveClassName={{
                      institutionLogo: 'mobile-tablet:size-20',
                      institutionName:
                        'mobile-tablet:typo-caption1 mobile-tablet:font-600',
                      productName:
                        'mobile-tablet:typo-body4 mobile-tablet:font-500',
                      donationAmount:
                        'mobile-tablet:typo-body3 mobile-tablet:font-700',
                      donationAmountText:
                        'mobile-tablet:typo-body4 mobile-tablet:font-600',
                    }}
                  />
                </li>
              ),
            )}

            {isLoadingFirst &&
              Array.from({ length: 10 }).map((_, index) => (
                <li key={index}>
                  <GiftListItem isLoading />
                </li>
              ))}
          </ul>

          {(hasNextFirstPage || hasNextSecondPage) && (
            <div ref={ref} className="flex justify-center py-24">
              <Spinner size={24} />
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col">
          <div
            className={cn(
              'py-60 text-center font-500 text-Gray-550 flex-center typo-h5',
              'mobile-tablet:py-48 mobile-tablet:font-500 mobile-tablet:typo-body4',
            )}
          >
            <p>
              <span className={cn('font-800', 'mobile-tablet:font-700')}>
                &apos;{searchKeyword}&apos;
              </span>
              에 대한 검색 결과가 없어요.
              <br />
              다른 키워드로 검색해 보세요.
            </p>
          </div>

          <SearchPrepareSection
            onHistoryClick={onHistoryClick}
            onRecommendClick={onRecommendClick}
            className="hidden mobile-tablet:flex"
          />
        </div>
      )}
    </section>
  );
};

const INITIAL_PAGE = 1;
const PAGE_SIZE = 5;
